import {
  componentsRaw,
  FlatTokenMap,
  TokenPrimitive,
  modeDarkRaw,
  modeLightRaw,
  themeRaw,
} from './raw';

export const DEFAULT_VALUE_KEY = '__default';

export type TokenTree = {
  readonly [key: string]: TokenPrimitive | TokenTree;
};

export type TokenAccessTree = {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  readonly [key: string]: any;
};

type ContextMaps = Record<string, FlatTokenMap>;

interface BuildTreeOptions {
  splitOnHyphenAlpha?: boolean;
}

const themeResolved = resolveReferences(themeRaw);
const modeResolved = {
  light: resolveReferences(modeLightRaw, { theme: themeResolved }),
  dark: resolveReferences(modeDarkRaw, { theme: themeResolved }),
} as const;

const componentsResolved = {
  light: resolveReferences(componentsRaw, {
    mode: modeResolved.light,
    theme: themeResolved,
  }),
  dark: resolveReferences(componentsRaw, {
    mode: modeResolved.dark,
    theme: themeResolved,
  }),
} as const;

const themeTree = deepFreeze(buildTokenTree(themeResolved, { splitOnHyphenAlpha: true }));
const modesTree = {
  light: deepFreeze(buildTokenTree(modeResolved.light, { splitOnHyphenAlpha: true })),
  dark: deepFreeze(buildTokenTree(modeResolved.dark, { splitOnHyphenAlpha: true })),
} as const;
const componentsTree = {
  light: deepFreeze(buildTokenTree(componentsResolved.light, { splitOnHyphenAlpha: true })),
  dark: deepFreeze(buildTokenTree(componentsResolved.dark, { splitOnHyphenAlpha: true })),
} as const;

export const theme = themeTree as TokenAccessTree;
export const modes = modesTree as {
  readonly [K in keyof typeof modesTree]: TokenAccessTree;
};
export const components = componentsTree as {
  readonly [K in keyof typeof componentsTree]: TokenAccessTree;
};

export type ModeName = keyof typeof modes;
export type ModeTokenTree = (typeof modes)[ModeName];

export type DesignTokensBundle = Readonly<
  TokenAccessTree &
    ModeTokenTree & {
      readonly mode: ModeName;
      readonly theme: TokenAccessTree;
      readonly components: TokenAccessTree;
      readonly flat: {
        readonly theme: FlatTokenMap;
        readonly mode: FlatTokenMap;
        readonly components: FlatTokenMap;
      };
      get(path: string): TokenPrimitive | TokenTree | undefined;
    }
>;

export function createDesignTokens(mode: ModeName): DesignTokensBundle {
  const bundleTheme = theme as TokenAccessTree;
  const bundleMode = modes[mode];
  const bundleComponents = components[mode];

  const scope: TokenTree = {
    theme: themeTree,
    components: componentsTree[mode],
    mode: modesTree[mode],
    ...modesTree[mode],
  };

  const base = {
    mode,
    theme: bundleTheme,
    components: bundleComponents,
    flat: {
      theme: themeResolved,
      mode: modeResolved[mode],
      components: componentsResolved[mode],
    },
    get(path: string) {
      return getToken(scope, path);
    },
  };

  return Object.freeze(Object.assign({}, bundleMode, base)) as DesignTokensBundle;
}

export function getToken(
  tree: TokenTree | TokenAccessTree,
  path: string,
): TokenPrimitive | TokenTree | undefined {
  if (!path) {
    return tree;
  }

  const segments = path.split('.').filter(Boolean);
  let current: TokenPrimitive | TokenTree = tree;

  for (const segment of segments) {
    if (typeof current !== 'object' || current === null) {
      return undefined;
    }
    const next: TokenPrimitive | TokenTree = (current as TokenTree)[segment];
    if (next === undefined) {
      return undefined;
    }
    current = next;
  }

  if (typeof current === 'object' && current !== null) {
    const container = current as Record<string, unknown>;
    if (DEFAULT_VALUE_KEY in container) {
      return container[DEFAULT_VALUE_KEY] as TokenPrimitive;
    }
  }

  return current;
}

export function resolveReferences(flat: FlatTokenMap, context: ContextMaps = {}): FlatTokenMap {
  const memo = new Map<string, TokenPrimitive>();
  const resolved: FlatTokenMap = {};

  const resolveKey = (key: string, stack: Set<string> = new Set()): TokenPrimitive => {
    const memoValue = memo.get(key);
    if (memoValue !== undefined) {
      return memoValue;
    }

    if (stack.has(key)) {
      throw new Error(`Circular reference detected for token "${key}"`);
    }

    if (!(key in flat)) {
      throw new Error(`Unknown token "${key}"`);
    }

    stack.add(key);
    const rawValue = flat[key];
    const value = resolveValue(rawValue, context, new Set<string>(), (selfKey) =>
      resolveKey(selfKey, stack),
    );
    stack.delete(key);

    memo.set(key, value);
    return value;
  };

  for (const key of Object.keys(flat)) {
    resolved[key] = resolveKey(key);
  }

  return resolved;
}

function resolveValue(
  value: TokenPrimitive,
  context: ContextMaps,
  visited: Set<string>,
  selfResolver: (key: string) => TokenPrimitive,
): TokenPrimitive {
  if (typeof value !== 'string' || !value.startsWith('$')) {
    return value;
  }

  const reference = value.slice(1);
  const [prefix, ...rest] = reference.split('/');

  if (!prefix || rest.length === 0) {
    throw new Error(`Invalid token reference "${value}"`);
  }

  if (prefix === 'self') {
    return selfResolver(rest.join('/'));
  }

  const source = context[prefix];
  if (!source) {
    throw new Error(`Unknown token namespace "${prefix}" in reference "${value}"`);
  }

  const targetKey = rest.join('/');
  const visitKey = `${prefix}:${targetKey}`;

  if (visited.has(visitKey)) {
    throw new Error(`Circular reference detected via "${value}"`);
  }

  if (!(targetKey in source)) {
    throw new Error(
      `Missing token "${targetKey}" in namespace "${prefix}" referenced by "${value}"`,
    );
  }

  visited.add(visitKey);
  const targetValue = source[targetKey];
  const result = resolveValue(targetValue, context, visited, selfResolver);
  visited.delete(visitKey);

  return result;
}

function buildTokenTree(flat: FlatTokenMap, options: BuildTreeOptions): TokenTree {
  const target: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(flat)) {
    const segments = toPathSegments(key, options);
    insertValue(target, segments, value);
  }

  return target as TokenTree;
}

function insertValue(target: Record<string, unknown>, segments: string[], value: TokenPrimitive) {
  let current: Record<string, unknown> = target;

  segments.forEach((segment, index) => {
    const isLeaf = index === segments.length - 1;

    if (isLeaf) {
      const existing = current[segment];
      if (
        typeof existing === 'object' &&
        existing !== null &&
        DEFAULT_VALUE_KEY in (existing as Record<string, unknown>)
      ) {
        (existing as Record<string, unknown>)[DEFAULT_VALUE_KEY] = value;
        return;
      }

      if (existing === undefined) {
        current[segment] = value;
        return;
      }

      if (
        typeof existing === 'object' &&
        existing !== null &&
        !(DEFAULT_VALUE_KEY in (existing as Record<string, unknown>))
      ) {
        (existing as Record<string, unknown>)[DEFAULT_VALUE_KEY] = value;
        return;
      }

      if (existing === value) {
        return;
      }

      const container: Record<string, unknown> = {
        [DEFAULT_VALUE_KEY]: existing,
      };
      current[segment] = container;
      container[DEFAULT_VALUE_KEY] = value;
      return;
    }

    const existing = current[segment];

    if (existing === undefined) {
      const child: Record<string, unknown> = {};
      current[segment] = child;
      current = child;
      return;
    }

    if (typeof existing !== 'object' || existing === null) {
      const container: Record<string, unknown> = {
        [DEFAULT_VALUE_KEY]: existing,
      };
      current[segment] = container;
      current = container;
    } else {
      current = existing as Record<string, unknown>;
    }
  });
}

function toPathSegments(key: string, options: BuildTreeOptions): string[] {
  const baseSegments = key.split('/');
  const result: string[] = [];

  for (const base of baseSegments) {
    const expanded = expandSegment(base, result, options);
    for (const part of expanded) {
      result.push(sanitizeSegment(part));
    }
  }

  return result;
}

function expandSegment(
  segment: string,
  currentPath: string[],
  options: BuildTreeOptions,
): string[] {
  if (!options.splitOnHyphenAlpha || !segment.includes('-')) {
    return [segment];
  }

  const parts = segment.split('-').filter(Boolean);
  if (parts.length <= 1) {
    return [segment];
  }

  const expanded: string[] = [];
  const previous = currentPath[currentPath.length - 1];

  parts.forEach((part, index) => {
    const sanitizedPart = sanitizeSegment(part);
    if (index === 0 && sanitizedPart === previous) {
      return;
    }

    expanded.push(part);
  });

  return expanded.length > 0 ? expanded : [segment];
}

function sanitizeSegment(segment: string): string {
  if (!segment) {
    return '_';
  }

  const parts = segment
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((part) => part.toLowerCase());

  if (parts.length === 0) {
    return '_';
  }

  const [first, ...rest] = parts;
  let sanitized = first;

  rest.forEach((part) => {
    sanitized += part.charAt(0).toUpperCase() + part.slice(1);
  });

  return sanitized;
}

function deepFreeze<T>(input: T): T {
  if (typeof input !== 'object' || input === null) {
    return input;
  }

  Object.values(input).forEach((value) => {
    deepFreeze(value);
  });

  return Object.freeze(input);
}
